import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { PrintOnEnum } from '../printOn';
import LabelPreviewData from './LabelPreview';
import SheetType from './SheetType.enum';

/**
 * Documentation:
 * https://stamps.atlassian.net/wiki/spaces/RnD/pages/1078919319/LabelPreview+Component
 */
export default class LabelPreviewHelper {
	/**
	 * Return static data object.
	 *
	 * @param {SheetType} sheet Sheet value.
	 * @param {string} series Optional. Required when sheet has different series.
	 * @returns {object|null}
	 */
	static getData(sheet, series) {
		const sheetData = find(LabelPreviewData, { sheet });
		let seriesData = null;

		if (sheet === SheetType.NetStamps) {
			seriesData = isArray(sheetData?.series)
				? this.getNetstampParameters(sheetData, series)
				: null;
		} else {
			seriesData = isArray(sheetData?.series)
				? find(sheetData.series, { type: series })
				: null;
		}

		return isObject(seriesData)
			? {
					...seriesData,
					sheet,
				}
			: null;
	}

	/**
	 * Return matched netstamps pattern of series data object.
	 *
	 * @param {string} series Optional. Required when sheet has different series.
	 * @returns {object|null}
	 */
	static getNetstampParameters = (sheetData, series) => {
		if (isString(series) && series !== '') {
			for (const netstamps of sheetData.series) {
				if (netstamps.pattern.test(series) === true) {
					return netstamps;
				}
			}
		}

		return null;
	};

	/**
	 * Return SheetType value based on print-on selection
	 *
	 * @param {PrintOnEnum} printOn
	 * @returns {SheetType}
	 */
	static getSheetType = printOn => {
		switch (printOn) {
			case PrintOnEnum.ShippingLabel812x11:
			case PrintOnEnum.ShippingLabel1200:
			case PrintOnEnum.ShippingLabel512x812:
				return SheetType.ShippingLabel;

			case PrintOnEnum.EnvelopeA2:
			case PrintOnEnum.EnvelopeA9:
			case PrintOnEnum.Envelope6:
			case PrintOnEnum.Envelope7:
			case PrintOnEnum.Envelope9:
			case PrintOnEnum.Envelope10:
			case PrintOnEnum.Envelope11:
			case PrintOnEnum.Envelope12:
				return SheetType.Envelope;

			case PrintOnEnum.MailingLabelCM3610:
			case PrintOnEnum.MailingLabelCM3710:
			case PrintOnEnum.MailingLabelCM3910:
			case PrintOnEnum.MailingLabelCM3930:
			case PrintOnEnum.EnvelopeCM3810:
			case PrintOnEnum.EnvelopeCM3830:
				return SheetType.CertifiedMail;

			case PrintOnEnum.Roll4x6:
			case PrintOnEnum.Roll418x614:
				return SheetType.Roll;

			case PrintOnEnum.NetStamps:
			default:
				return SheetType.NetStamps;
		}
	};

	/**
	 * Return series value based on print-on selection.
	 *
	 * @param {PrintOnEnum} printOn
	 * @param {string} defaultSeries Default series value to fall back to
	 * @returns {string}
	 */
	static getSeries = (printOn, defaultSeries = '') => {
		switch (printOn) {
			case PrintOnEnum.EnvelopeA2:
				return 'a2';
			case PrintOnEnum.EnvelopeA9:
				return 'a9';
			case PrintOnEnum.Envelope6:
				return '6';
			case PrintOnEnum.Envelope7:
				return '7';
			case PrintOnEnum.Envelope9:
				return '9';
			case PrintOnEnum.Envelope10:
				return '10';
			case PrintOnEnum.Envelope11:
				return '11';
			case PrintOnEnum.Envelope12:
				return '12';
			case PrintOnEnum.MailingLabelCM3610:
				return '3610';
			case PrintOnEnum.MailingLabelCM3710:
				return '3710';
			case PrintOnEnum.MailingLabelCM3910:
				return '3910';
			case PrintOnEnum.MailingLabelCM3930:
				return '3930';
			case PrintOnEnum.EnvelopeCM3810:
				return '3810';
			case PrintOnEnum.EnvelopeCM3830:
				return '3830';

			default:
				return defaultSeries;
		}
	};
}
